import { BREAKPOINT } from '@/constants/enum';
import useHandleContextual from '@/hooks/useHandleContextureBar';
import appbarSlice, { clickedDiscardSelector } from '@/redux/slice/appbar.slice';
import errorSlice from '@/redux/slice/error.slice';
import navigateSlice, { navigateSelector } from '@/redux/slice/navigate';
import { Button } from '@shopify/polaris';
import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import BaseModal from '../BaseModal';
import RegularText from '../RegularText';
import { ContextualBarStyled } from './styled';

interface Props {
  saveAction: () => void;
  loadingSave: boolean;
  discardAction: () => void;
  isVisible: boolean;
  isDisabled?: boolean;
  isDisabledDiscard?: boolean;
}

const ContextualBar = ({ ...props }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const path = useSelector(navigateSelector);
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });
  const isClickedDiscard = useSelector(clickedDiscardSelector);
  const contextualBar = useHandleContextual();
  const [modal, setModal] = useState({
    type: 'leavePage',
    status: false,
    title: 'You have unsaved changes',
    body: 'If you leave this page, all unsaved changes will be lost.',
    secondaryBtn: 'Cancel',
    primaryBtn: 'Leave page',
  });

  useEffect(() => {
    if (props.isVisible && !!path.pathname && !!path.search && modal.type === 'leavePage') {
      setModal({
        type: 'leavePage',
        status: true,
        title: 'You have unsaved changes',
        body: 'If you leave this page, all unsaved changes will be lost.',
        secondaryBtn: 'Cancel',
        primaryBtn: 'Leave page',
      });
    }
    if (!props.isVisible) {
      setModal({
        ...modal,
        status: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.type, path.pathname, path.search, props.isVisible]);

  useEffect(() => {
    if (isClickedDiscard) dispatch(appbarSlice.actions.handleUpdateDiscardState({ isClickedDiscard: false }));
  }, [isClickedDiscard, dispatch]);

  const clearDataModal = () => {
    dispatch(
      navigateSlice.actions.handleNavigate({
        pathname: undefined,
        search: undefined,
      }),
    );
    setModal({
      type: 'leavePage',
      status: false,
      title: 'You have unsaved changes',
      body: 'If you leave this page, all unsaved changes will be lost.',
      secondaryBtn: 'Cancel',
      primaryBtn: 'Leave page',
    });
  };

  const onDiscard = () => {
    setModal({
      type: 'discard',
      status: true,
      title: 'Discard all unsaved changes',
      body: 'If you discard changes, you’ll delete any edits you made since you last saved.',
      secondaryBtn: 'Continue editing',
      primaryBtn: 'Discard changes',
    });
  };

  const handleDiscard = () => {
    props.discardAction();
    dispatch(appbarSlice.actions.handleUpdateDiscardState({ isClickedDiscard: true }));
    dispatch(errorSlice.actions.handleIsDisabled(false));
    clearDataModal();
  };

  const handleLeavePage = () => {
    clearDataModal();
    props.discardAction();
    dispatch(errorSlice.actions.handleIsDisabled(false));
    navigate({ pathname: path.pathname, search: path.search }, { state: path.state });
    contextualBar.hide();
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: false }));
  };

  useEffect(() => {
    setTimeout(() => {
      if (props.isVisible) {
        contextualBar.show();
      } else {
        contextualBar.hide();
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(contextualBar), props.isVisible]);

  return (
    <>
      {/* {!isEmbedded ? ( */}
      {props.isVisible ? (
        <ContextualBarStyled>
          <Button onClick={onDiscard} loading={props.loadingSave} disabled={props.isDisabledDiscard}>
            Discard
          </Button>
          <div className="ml-8">
            <Button
              loading={props.loadingSave}
              onClick={() => {
                props.saveAction();
                clearDataModal();
                dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: false }));
              }}
              disabled={props.isDisabled}
            >
              Save
            </Button>
          </div>
        </ContextualBarStyled>
      ) : null}
      {/* ) : (
        <SaveBar id={EstElementId.SaveBar}>
          <button
            loading={props.loadingSave}
            variant="primary"
            onClick={() => {
              props.saveAction();
              clearDataModal();
              contextualBar.hide();
              dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: false }));
            }}
            disabled={props.isDisabled}
          ></button>
          <button
            onClick={() => {
              onDiscard();
            }}
            disabled={props.isDisabledDiscard}
          ></button>
        </SaveBar>
      )} */}
      <BaseModal
        open={modal.status}
        title={modal.title}
        onClose={clearDataModal}
        footer={
          <div className="content-right" style={{ width: isMobile ? 'calc(100vw - 3rem)' : '588px' }}>
            <Button onClick={clearDataModal}>{modal.secondaryBtn}</Button>
            <div className="ml-8">
              <Button tone="critical" variant="primary" onClick={modal.type === 'discard' ? handleDiscard : handleLeavePage}>
                {modal.primaryBtn}
              </Button>
            </div>
          </div>
        }
      >
        <RegularText>{modal.body}</RegularText>
      </BaseModal>
    </>
  );
};

export default memo(ContextualBar);
